import {FC, useEffect, useState} from 'react';
import DashboardLayout from "@/components/layouts/Dashboard";
import Card from "@/components/Card";
import * as Yup from 'yup';
import {Field, Form, Formik} from 'formik';
import {FormContainer, FormItem} from '@/components/ui/Form/index';
import {
  apiGetUserInfo,
  apiUpdateUserInfo,
  apiChangePasswordInfo
} from '@/services/UserService';
import {
  apiGetCustomerPortalUrl
} from '@/services/ProductSubscriptionService';
import {Input} from "@/components/ui/Input/index";
import {Button} from "@/components/ui/Button/index";
import Alert from "@/components/ui/Alert/index";
import {Checkbox} from "@/components/ui/checkbox";
import {
  AlertCircle,
  CheckIcon,
  CreditCard,
  ExternalLink,
  Github,
  Info
} from "lucide-react";
import {Link} from "react-router-dom";

interface SettingsProps {
}

// Updated UserInfo type to include new subscription status
type UserInfo = {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  token: string;
  endPoint: string;
  subscriptionName: string;
  subscriptionStatus: 'NO_SUBSCRIPTION' | 'ACTIVE' | 'ACTIVE_UNTIL_PERIOD_END' | 'PAST_DUE' | 'CANCELED';
  dueDate: string;
  cancellationRequestedDate?: string;
};

type UpdateUserFormSchema = {
  firstName: string;
  lastName: string;
  token: string;
  endPoint: string;
};

type ChangePasswordFormSchema = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type ResponseStatus = {
  status: 'SECCESS' | 'FAILED';
  message: string;
};

type PortalResponse = {
  redirectUrl: string;
};

const Settings: FC<SettingsProps> = () => {
  const [initialUserValues, setInitialUserValues] = useState<UpdateUserFormSchema>({
    firstName: '',
    lastName: '',
    token: '',
    endPoint: '',
  });
  
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [updateUserInfoResponseStatus, setUpdateUserInfoResponseStatus] = useState<ResponseStatus | undefined>();
  const [changePasswordResponseStatus, setChangePasswordResponseStatus] = useState<ResponseStatus | undefined>();
  const [userInfoError, setUserInfoError] = useState<string | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [useGitHubEnterprise, setUseGitHubEnterprise] = useState(false);
  
  useEffect(() => {
    fetchUserInfo();
    // Don't fetch portal URL on page load - will be fetched on demand
  }, []);
  
  const validationUpdateUserInfoSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
  });
  
  const validationChangePasswordSchema = Yup.object().shape({
    oldPassword: Yup.string().required('Old password is required'),
    newPassword: Yup.string().required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Confirm password is required'),
  });
  
  const handleGithubTokenCreation = () => {
    const githubTokenUrl = "https://github.com/settings/tokens/new?scopes=repo,delete_repo&description=API%20Access%20Token";
    window.open(githubTokenUrl, '_blank');
  };
  
  const fetchUserInfo = async () => {
    setIsLoading(true);
    setUserInfoError(null);
    try {
      const result = await apiGetUserInfo();
      // Use type assertion to handle the response data
      const apiData = result.data as any;
      
      // Map API response to UserInfo type with proper type safety
      const userData: UserInfo = {
        id: apiData.id || '',
        username: apiData.username || apiData.email || '',
        firstName: apiData.firstName || '',
        lastName: apiData.lastName || '',
        token: apiData.token || '',
        endPoint: apiData.endPoint || '',
        subscriptionName: apiData.subscriptionName || 'Free',
        subscriptionStatus: apiData.subscriptionStatus || 'NO_SUBSCRIPTION',
        dueDate: apiData.dueDate || '',
        cancellationRequestedDate: apiData.cancellationRequestedDate
      };
      
      setUserInfo(userData);
      setInitialUserValues({
        firstName: userData.firstName,
        lastName: userData.lastName,
        token: userData.token,
        endPoint: userData.endPoint,
      });
      // Set GitHub Enterprise checkbox based on whether endPoint has a value
      setUseGitHubEnterprise(!!userData.endPoint && userData.endPoint.trim() !== '');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to load user information. Please try again.";
      setUserInfoError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePortalAccess = async () => {
    setIsPortalLoading(true);
    setPortalError(null);
    try {
      const result = await apiGetCustomerPortalUrl<PortalResponse>();
      // Redirect the user directly to the portal URL
      window.location.href = result.data.redirectUrl;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to access customer portal. Please try again.";
      setPortalError(errorMessage);
      setIsPortalLoading(false); // Only set to false if there's an error, otherwise page will redirect
    }
  };
  
  const onUpdateUserInfo = async (
    values: UpdateUserFormSchema,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    const {firstName, lastName, token, endPoint} = values;
    setSubmitting(true);
    setUpdateUserInfoResponseStatus(undefined);
    
    try {
      const result = await apiUpdateUserInfo({firstName, lastName, token, endPoint});
      if (result.status === 200) {
        setUpdateUserInfoResponseStatus({status: 'SECCESS', message: 'User information updated successfully'});
        // Refresh user info after update
        fetchUserInfo();
      } else {
        setUpdateUserInfoResponseStatus({
          status: 'FAILED',
          message: 'An error occurred while updating user information'
        });
      }
    } catch (err: any) {
      if ('message' in err.response.data) {
        setUpdateUserInfoResponseStatus({status: 'FAILED', message: err.response.data.message});
      } else {
        setUpdateUserInfoResponseStatus({
          status: 'FAILED',
          message: 'An error occurred while updating user information'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  const onChangePassword = async (
    values: ChangePasswordFormSchema,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    const {oldPassword, newPassword, confirmPassword} = values;
    setSubmitting(true);
    setChangePasswordResponseStatus(undefined);
    
    try {
      const response = await apiChangePasswordInfo({oldPassword, newPassword, confirmPassword});
      if (response.status === 200) {
        setChangePasswordResponseStatus({status: 'SECCESS', message: 'Password changed successfully'});
      } else {
        setChangePasswordResponseStatus({status: 'FAILED', message: 'An error occurred while changing the password'});
      }
    } catch (err: any) {
      if ('message' in err.response.data) {
        setChangePasswordResponseStatus({status: 'FAILED', message: err.response.data.message});
      } else {
        setChangePasswordResponseStatus({status: 'FAILED', message: 'An error occurred while changing the password'});
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Updated subscription status display with new states
  const renderSubscriptionStatus = () => {
    if (!userInfo) return null;
    
    const status = userInfo.subscriptionStatus;
    const dueDate = userInfo.dueDate;
    const cancellationDate = userInfo.cancellationRequestedDate;
    
    let statusBadge;
    let statusDescription;
    let actions;
    
    switch (status) {
      case 'ACTIVE':
        statusBadge = (
          <div className="inline-block px-4 py-1 bg-green-100 text-green-800 font-medium rounded-full text-sm">
            Active
          </div>
        );
        statusDescription = (
          <p className="text-gray-600 py-1">
            Your <span className="font-medium">{userInfo.subscriptionName || 'subscription'}</span> is active and will
            renew on {formatDate(dueDate)}.
          </p>
        );
        actions = (
          <Button
            variant="solid"
            onClick={handlePortalAccess}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center"
            icon={<ExternalLink className="h-4 w-4 mr-2"/>}
            loading={isPortalLoading}
            disabled={isPortalLoading}
          >
            {isPortalLoading ? 'Opening Portal...' : 'Manage Subscription'}
          </Button>
        );
        break;
      
      case 'ACTIVE_UNTIL_PERIOD_END':
        statusBadge = (
          <div className="inline-block px-4 py-1 bg-yellow-100 text-yellow-800 font-medium rounded-full text-sm">
            Canceling Soon
          </div>
        );
        statusDescription = (
          <div>
            <p className="text-gray-600 py-1">
              Your <span className="font-medium">{userInfo.subscriptionName || 'subscription'}</span> is currently
              active but scheduled to be canceled on {formatDate(dueDate)}.
            </p>
            {cancellationDate && (
              <p className="text-sm text-gray-500">
                Cancellation requested on {formatDate(cancellationDate)}
              </p>
            )}
          </div>
        );
        actions = (
          <Button
            variant="solid"
            onClick={handlePortalAccess}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center"
            icon={<ExternalLink className="h-4 w-4 mr-2"/>}
            loading={isPortalLoading}
            disabled={isPortalLoading}
          >
            {isPortalLoading ? 'Opening Portal...' : 'Manage Subscription'}
          </Button>
        );
        break;
      
      case 'CANCELED':
        statusBadge = (
          <div className="inline-block px-4 py-1 bg-red-100 text-red-800 font-medium rounded-full text-sm">
            Canceled
          </div>
        );
        statusDescription = (
          <p className="text-gray-600">
            Your subscription has been canceled. Subscribe again to continue using our services.
          </p>
        );
        actions = (
          <Link to="/pricing/plans">
            <Button
              variant="solid"
              className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center"
              icon={<CreditCard className="h-5 w-5 mr-2"/>}
            >
              Subscribe Now
            </Button>
          </Link>
        );
        break;
      
      case 'PAST_DUE':
        statusBadge = (
          <div className="inline-block px-4 py-1 bg-orange-100 text-orange-800 font-medium rounded-full text-sm">
            Payment Past Due
          </div>
        );
        statusDescription = (
          <p className="text-gray-600">
            Your subscription payment is past due. Please update your payment information.
          </p>
        );
        actions = (
          <Button
            variant="solid"
            onClick={handlePortalAccess}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center"
            icon={<ExternalLink className="h-4 w-4 mr-2"/>}
            loading={isPortalLoading}
            disabled={isPortalLoading}
          >
            {isPortalLoading ? 'Opening Portal...' : 'Update Payment Method'}
          </Button>
        );
        break;
      
      case 'NO_SUBSCRIPTION':
      default:
        statusBadge = (
          <div className="inline-block px-4 py-1 bg-gray-100 text-gray-800 font-medium rounded-full text-sm">
            No Subscription
          </div>
        );
        statusDescription = (
          <p className="text-gray-600 mb-2">
            You don't have an active subscription. Subscribe to start using our services.
          </p>
        );
        actions = (
          <Link to="/pricing/plans">
            <Button
              variant="solid"
              className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center"
              icon={<CreditCard className="h-5 w-5 mr-2"/>}
            >
              Subscribe Now
            </Button>
          </Link>
        );
        break;
    }
    
    return (
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-xl font-semibold text-gray-800">Subscription Status:</h3>
          {statusBadge}
        </div>
        
        {statusDescription}
        {actions}
      </div>
    );
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl m-auto flex flex-col items-stretch gap-5 h-full w-full grow">
        <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="full" grow>
          <div className='my-8 mx-4'>
            <h2 className="text-2xl font-bold text-indigo-700 mb-6 mt-4 ml-4">
              Subscription Information
            </h2>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 lg:col-span-8 lg:col-start-3">
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-700"></div>
                  </div>
                ) : userInfoError ? (
                  <Alert
                    showIcon
                    className="mb-4"
                    customIcon={<AlertCircle className="h-6 w-6"/>}
                    type="danger"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">Error loading user information</span>
                      <span>{userInfoError}</span>
                      <Button
                        variant="plain"
                        className="text-indigo-600 mt-2 self-start"
                        onClick={fetchUserInfo}
                      >
                        Try Again
                      </Button>
                    </div>
                  </Alert>
                ) : (
                  <>
                    {renderSubscriptionStatus()}
                    {portalError && (
                      <Alert
                        showIcon
                        className="mt-4"
                        customIcon={<AlertCircle className="h-6 w-6"/>}
                        type="danger"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">Error accessing customer portal</span>
                          <span>{portalError}</span>
                        </div>
                      </Alert>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-indigo-700 mb-6 mt-12 ml-4">
              Personal Information
            </h2>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 lg:col-span-8 lg:col-start-3">
                {/* Account Information */}
                {userInfo && (
                  <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-gray-500 w-32">Email:</span>
                      <span className="font-medium">{userInfo.username}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      You cannot change your email address as it serves as your username.
                    </div>
                  </div>
                )}
                
                {/* Update User Info Form */}
                <Formik
                  enableReinitialize
                  initialValues={initialUserValues}
                  validationSchema={validationUpdateUserInfoSchema}
                  onSubmit={(values, {setSubmitting}) =>
                    onUpdateUserInfo(values, setSubmitting)
                  }
                >
                  {({errors, touched, isSubmitting, setFieldValue}) => (
                    <Form className="space-y-6">
                      <FormContainer>
                        {updateUserInfoResponseStatus && (
                          <Alert
                            showIcon
                            closable={true}
                            className="mb-4"
                            customIcon={
                              updateUserInfoResponseStatus.status === 'SECCESS' ? <CheckIcon className="h-6 w-6"/> :
                                <AlertCircle className="h-6 w-6"/>
                            }
                            type={updateUserInfoResponseStatus.status === 'SECCESS' ? 'success' : 'danger'}
                          >
                            {updateUserInfoResponseStatus.message}
                          </Alert>
                        )}
                        <FormItem
                          label="First Name"
                          labelClass="text-xl text-[#6F6AF8] font-medium mb-0"
                          htmlFor="firstName"
                          invalid={!!(errors.firstName && touched.firstName)}
                          errorMessage={errors.firstName}
                        >
                          <Field
                            type="text"
                            autoComplete="given-name"
                            name="firstName"
                            placeholder="Enter your first name"
                            component={Input}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </FormItem>
                        
                        <FormItem
                          label="Last Name"
                          labelClass="text-xl text-[#6F6AF8] font-medium mb-0"
                          htmlFor="lastName"
                          invalid={!!(errors.lastName && touched.lastName)}
                          errorMessage={errors.lastName}
                        >
                          <Field
                            type="text"
                            autoComplete="family-name"
                            name="lastName"
                            placeholder="Enter your last name"
                            component={Input}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </FormItem>
                        
                        {/* GitHub Integration Section */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                          <div className="flex items-start space-x-3">
                            <Github className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-blue-900 mb-2">GitHub Integration</h3>
                              <p className="text-blue-800 text-sm mb-3">
                                Connect your GitHub account to enable repository creation and code publishing features. 
                                We need a personal access token with specific permissions to manage repositories on your behalf.
                              </p>
                              
                              {/* Security and Usage Information */}
                              <div className="bg-blue-100 border border-blue-200 rounded-md p-4 mb-4">
                                <div className="flex items-start space-x-2">
                                  <div className="flex-shrink-0">
                                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2">🔒 Security & Privacy</h4>
                                    <ul className="text-xs text-blue-800 space-y-1 mb-3">
                                      <li>• <strong>Encrypted Storage:</strong> Your token is encrypted using industry-standard AES-256 encryption and stored securely</li>
                                      <li>• <strong>Limited Access:</strong> We never access your private repositories or personal data beyond what's needed</li>
                                      <li>• <strong>Revocable:</strong> You can revoke this token anytime from your GitHub settings</li>
                                    </ul>
                                    
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2">⚡ Token Usage</h4>
                                    <p className="text-xs text-blue-800 mb-2">Your GitHub token will <strong>only</strong> be used for:</p>
                                    <ul className="text-xs text-blue-800 space-y-1">
                                      <li>• <strong>Creating repositories</strong> to host your generated code</li>
                                      <li>• <strong>Pushing code changes</strong> and updates to your repositories</li>
                                      <li>• <strong>Managing pull requests</strong> for code reviews and collaboration</li>
                                    </ul>
                                    <p className="text-xs text-blue-700 mt-2 italic">
                                      We will never read your existing code or access other repositories, or perform any other actions that are not listed above.
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              <FormItem
                                label="GitHub Personal Access Token"
                                labelClass="text-base font-medium text-blue-900 mb-2"
                                htmlFor="token"
                                invalid={!!(errors.token && touched.token)}
                                errorMessage={errors.token}
                              >
                                <Field
                                  autoComplete="off"
                                  name="token"
                                  placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                  component={Input}
                                  className="mt-1 block w-full shadow-sm sm:text-sm border-blue-300 rounded-md"
                                />
                                
                                <div className="mt-3 bg-white border border-blue-200 rounded-md p-3">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm font-medium text-blue-900">Need a GitHub token?</p>
                                      <p className="text-xs text-blue-700 mt-1">
                                        Click below to create a token with the correct permissions (repo, delete_repo)
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="solid"
                                      size="sm"
                                      onClick={handleGithubTokenCreation}
                                      className="bg-blue-600 hover:bg-blue-700 text-white flex items-center space-x-2"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                      <span>Create GitHub Token</span>
                                    </Button>
                                  </div>
                                </div>
                              </FormItem>
                              
                              {/* GitHub Enterprise Toggle */}
                              <div className="mt-4 flex items-start space-x-3">
                                <Checkbox
                                  id="github-enterprise"
                                  checked={useGitHubEnterprise}
                                  onCheckedChange={(checked) => {
                                    setUseGitHubEnterprise(checked as boolean);
                                    // Clear endPoint field when unchecking GitHub Enterprise
                                    if (!checked) {
                                      setFieldValue('endPoint', '');
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <label 
                                    htmlFor="github-enterprise" 
                                    className="text-sm font-medium text-blue-900 cursor-pointer"
                                  >
                                    I use GitHub Enterprise
                                  </label>
                                  <p className="text-xs text-blue-700 mt-1">
                                    Check this if you're using a self-hosted GitHub Enterprise instance instead of github.com
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* GitHub Enterprise Endpoint - Only show if checkbox is checked */}
                        {useGitHubEnterprise && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                            <div className="flex items-start space-x-3">
                              <Info className="h-5 w-5 text-yellow-600 mt-1 flex-shrink-0" />
                              <div className="flex-1">
                                <h4 className="text-base font-semibold text-yellow-900 mb-2">GitHub Enterprise Configuration</h4>
                                <p className="text-yellow-800 text-sm mb-4">
                                  Since you're using GitHub Enterprise, please provide your custom API endpoint URL. 
                                  This should be the base URL for your GitHub Enterprise API.
                                </p>
                                
                                <FormItem
                                  label="GitHub Enterprise API Endpoint"
                                  labelClass="text-base font-medium text-yellow-900 mb-2"
                                  htmlFor="endPoint"
                                  invalid={!!(errors.endPoint && touched.endPoint)}
                                  errorMessage={errors.endPoint}
                                >
                                  <Field
                                    type="url"
                                    autoComplete="off"
                                    name="endPoint"
                                    placeholder="https://github.your-company.com/api/v3"
                                    component={Input}
                                    className="mt-1 block w-full shadow-sm sm:text-sm border-yellow-300 rounded-md"
                                  />
                                  <div className="mt-2 bg-white border border-yellow-200 rounded-md p-3">
                                    <p className="text-xs font-medium text-yellow-900 mb-1">Examples of valid endpoints:</p>
                                    <ul className="text-xs text-yellow-800 space-y-1">
                                      <li>• <code className="bg-yellow-100 px-1 rounded">https://github.your-company.com/api/v3</code></li>
                                      <li>• <code className="bg-yellow-100 px-1 rounded">https://api.github.your-company.com</code></li>
                                      <li>• <code className="bg-yellow-100 px-1 rounded">https://your-domain.com/github/api/v3</code></li>
                                    </ul>
                                  </div>
                                </FormItem>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-4 text-right">
                          <Button
                            loading={isSubmitting}
                            variant="solid"
                            className="bg-indigo-600 tracking-wider text-lg font-semibold capitalize"
                            type="submit"
                          >
                            {isSubmitting ? 'Updating information...' : 'Update information'}
                          </Button>
                        </div>
                      </FormContainer>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-indigo-700 mb-6 mt-12 ml-4">
              Change Password
            </h2>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 lg:col-span-8 lg:col-start-3">
                {/* Change Password Form */}
                <Formik
                  initialValues={{
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                  }}
                  validationSchema={validationChangePasswordSchema}
                  onSubmit={(values, {setSubmitting}) =>
                    onChangePassword(values, setSubmitting)
                  }
                >
                  {({errors, touched, isSubmitting}) => (
                    <Form className="space-y-6">
                      <FormContainer>
                        {changePasswordResponseStatus && (
                          <Alert
                            showIcon
                            closable={true}
                            className="mb-4"
                            customIcon={
                              changePasswordResponseStatus.status === 'SECCESS' ? <CheckIcon className="h-6 w-6"/> :
                                <AlertCircle className="h-6 w-6"/>
                            }
                            type={changePasswordResponseStatus.status === 'SECCESS' ? 'success' : 'danger'}
                          >
                            {changePasswordResponseStatus.message}
                          </Alert>
                        )}
                        <FormItem
                          label="Old Password"
                          labelClass="text-xl text-[#6F6AF8] font-medium mb-0"
                          htmlFor="oldPassword"
                          invalid={!!(errors.oldPassword && touched.oldPassword)}
                          errorMessage={errors.oldPassword}
                        >
                          <Field
                            type="password"
                            autoComplete="current-password"
                            name="oldPassword"
                            placeholder="Enter your old password"
                            component={Input}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </FormItem>
                        
                        <FormItem
                          label="New Password"
                          labelClass="text-xl text-[#6F6AF8] font-medium mb-0"
                          htmlFor="newPassword"
                          invalid={!!(errors.newPassword && touched.newPassword)}
                          errorMessage={errors.newPassword}
                        >
                          <Field
                            type="password"
                            autoComplete="new-password"
                            name="newPassword"
                            placeholder="Enter your new password"
                            component={Input}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </FormItem>
                        
                        <FormItem
                          label="Confirm Password"
                          labelClass="text-xl text-[#6F6AF8] font-medium mb-0"
                          htmlFor="confirmPassword"
                          invalid={!!(errors.confirmPassword && touched.confirmPassword)}
                          errorMessage={errors.confirmPassword}
                        >
                          <Field
                            type="password"
                            autoComplete="new-password"
                            name="confirmPassword"
                            placeholder="Confirm your new password"
                            component={Input}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </FormItem>
                        
                        <div className="mt-4 text-right">
                          <Button
                            loading={isSubmitting}
                            variant="solid"
                            className="bg-indigo-600 tracking-wider text-lg font-semibold capitalize"
                            type="submit"
                          >
                            {isSubmitting ? 'Changing Password...' : 'Change Password'}
                          </Button>
                        </div>
                      </FormContainer>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Settings;